import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { crmService } from '@/services/crmCRUD';
import {
  outboundWorkflowService,
  type OutboundStage,
  type OutboundWorkflow,
  type TodayRow,
} from '@/services/outboundWorkflowService';

const DISPOSITIONS = [
  'completed',
  'accepted',
  'replied_interested',
  'replied_not_interested',
  'nurture',
  'needs_review',
  'answered',
  'meeting_scheduled',
  'meeting_completed',
  'gift_sent',
  'resume',
  'extend',
  'disqualified',
  'do_not_contact',
];

function fillScript(script: string | null, row: TodayRow): string {
  if (!script) return '';
  return script.replace(/\{first_name\}/gi, row.first_name || '');
}

export const OutboundTodayPage: React.FC = () => {
  const { t } = useLanguage();
  const [rows, setRows] = useState<TodayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await outboundWorkflowService.today());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load Today');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => {
    const order: TodayRow['bucket'][] = ['overdue', 'due_today', 'nurture_review'];
    return order
      .map((bucket) => ({
        bucket,
        title:
          bucket === 'overdue'
            ? t('outbound.today.overdue')
            : bucket === 'nurture_review'
              ? t('outbound.today.nurtureReview')
              : t('outbound.today.dueToday'),
        items: rows.filter((r) => r.bucket === bucket),
      }))
      .filter((g) => g.items.length > 0);
  }, [rows, t]);

  const complete = async (enrollmentId: string, disposition: string) => {
    await outboundWorkflowService.complete(enrollmentId, disposition);
    await load();
  };

  const copyScript = async (row: TodayRow) => {
    const text = fillScript(row.script_text, row);
    if (text) await navigator.clipboard.writeText(text);
  };

  return (
    <main className="space-y-3">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-[var(--color-primary-font)]">{t('outbound.today.title')}</h1>
        <button
          type="button"
          className="rounded px-3 py-1.5 text-sm bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)]"
          onClick={() => setEnrollOpen(true)}
        >
          {t('outbound.today.enroll')}
        </button>
      </header>
      {loading && <p className="text-sm">{t('common.loading') || 'Loading…'}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}
      {!loading && rows.length === 0 && <p className="text-sm">{t('outbound.today.empty')}</p>}
      {groups.map((group) => (
        <section key={group.bucket} className="space-y-2">
          <h2 className="text-sm font-medium text-[var(--color-primary-font)]">{group.title}</h2>
          <div className="space-y-2">
            {group.items.map((row) => (
              <article
                key={row.enrollment_id}
                className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-3 space-y-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium">
                    {row.first_name} {row.last_name}
                    {row.company_name ? ` · ${row.company_name}` : ''}
                    {row.title ? ` · ${row.title}` : ''}
                  </p>
                  <p className="text-sm">{row.stage_name}</p>
                </div>
                {row.trigger_text && <p className="text-xs opacity-80">{row.trigger_text}</p>}
                {row.script_text && (
                  <pre className="whitespace-pre-wrap text-xs max-h-28 overflow-auto rounded bg-[var(--color-background)] p-2">
                    {fillScript(row.script_text, row)}
                  </pre>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  {row.linkedin_url && (
                    <a
                      href={row.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm underline"
                    >
                      {t('outbound.today.openLinkedIn')}
                    </a>
                  )}
                  <button type="button" className="text-sm underline" onClick={() => void copyScript(row)}>
                    {t('outbound.today.copyScript')}
                  </button>
                  <label className="text-sm flex items-center gap-1">
                    {t('outbound.today.complete')}
                    <select
                      className="border rounded px-1 py-0.5 text-sm"
                      defaultValue=""
                      onChange={(e) => {
                        const value = e.target.value;
                        e.target.value = '';
                        if (value) void complete(row.enrollment_id, value);
                      }}
                    >
                      <option value="">{t('outbound.today.complete')}</option>
                      {DISPOSITIONS.map((d) => (
                        <option key={d} value={d}>
                          {t(`outbound.disposition.${d}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
      {enrollOpen && <EnrollDialog onClose={() => setEnrollOpen(false)} onEnrolled={load} />}
    </main>
  );
};

const EnrollDialog: React.FC<{ onClose: () => void; onEnrolled: () => void }> = ({
  onClose,
  onEnrolled,
}) => {
  const { t } = useLanguage();
  const [workflows, setWorkflows] = useState<OutboundWorkflow[]>([]);
  const [stages, setStages] = useState<OutboundStage[]>([]);
  const [contacts, setContacts] = useState<Array<{ id: string; first_name: string; last_name: string; email?: string }>>(
    []
  );
  const [workflowId, setWorkflowId] = useState('');
  const [stageCode, setStageCode] = useState('up_next');
  const [contactId, setContactId] = useState('');

  useEffect(() => {
    void (async () => {
      const wfs = await outboundWorkflowService.listWorkflows();
      setWorkflows(wfs);
      const wf = wfs[0];
      if (wf) {
        setWorkflowId(wf.id);
        setStages(await outboundWorkflowService.listStages(wf.id));
      }
      const list = await crmService.getContacts({ limit: 200 });
      const items = Array.isArray(list?.data) ? list.data : Array.isArray(list) ? list : [];
      setContacts(items);
    })();
  }, []);

  const submit = async () => {
    if (!contactId) return;
    await outboundWorkflowService.enroll({
      contact_id: contactId,
      workflow_id: workflowId || undefined,
      stage_code: stageCode,
    });
    onEnrolled();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--color-surface)] rounded p-4 w-full max-w-md space-y-3">
        <h2 className="font-medium">{t('outbound.enroll.title')}</h2>
        <label className="block text-sm">
          Contact
          <select
            className="mt-1 w-full border rounded px-2 py-1"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
          >
            <option value="">Select…</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.last_name}
                {c.email ? ` (${c.email})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Workflow
          <select
            className="mt-1 w-full border rounded px-2 py-1"
            value={workflowId}
            onChange={(e) => {
              setWorkflowId(e.target.value);
              void outboundWorkflowService.listStages(e.target.value).then(setStages);
            }}
          >
            {workflows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Stage
          <select
            className="mt-1 w-full border rounded px-2 py-1"
            value={stageCode}
            onChange={(e) => setStageCode(e.target.value)}
          >
            {stages.map((s) => (
              <option key={s.id} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="text-sm px-3 py-1" onClick={onClose}>
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            className="text-sm px-3 py-1 rounded bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)]"
            onClick={() => void submit()}
          >
            {t('outbound.today.enroll')}
          </button>
        </div>
      </div>
    </div>
  );
};
